'use client';

import { createContext, useContext, useEffect, type ReactNode } from 'react';
import { create } from 'zustand';

import type { AccountTexts, CheckoutTexts } from '@eliadmin/core/src/utils/nt/checkout';

/**
 * Admin texts for client components (see `utils/nt/checkout.ts`). Pages that render the checkout or
 * the account pass them from the server through the providers below. The add-to-cart drawer opens
 * from any page in the core dialog slot (outside every page tree), so the header also hydrates the
 * same texts into a small store: `useCheckoutTexts()` reads the provider first, then the store.
 */
const CheckoutTextsContext = createContext<CheckoutTexts | null>(null);
const AccountTextsContext = createContext<AccountTexts | null>(null);

const useCheckoutTextsStore = create<{ texts: CheckoutTexts | null }>(() => ({ texts: null }));

export function CheckoutTextsProvider({
  texts,
  children,
}: {
  texts: CheckoutTexts;
  children: ReactNode;
}) {
  return <CheckoutTextsContext.Provider value={texts}>{children}</CheckoutTextsContext.Provider>;
}

export function AccountTextsProvider({
  texts,
  children,
}: {
  texts: AccountTexts;
  children: ReactNode;
}) {
  return <AccountTextsContext.Provider value={texts}>{children}</AccountTextsContext.Provider>;
}

/** Rendered once by the header: the drawer reads the checkout texts on every page. */
export function CheckoutTextsHydrator({ texts }: { texts: CheckoutTexts }) {
  useEffect(() => {
    useCheckoutTextsStore.setState({ texts });
  }, [texts]);
  return null;
}

/** Checkout texts inside the cart page / order confirmation (`CheckoutTextsProvider`). */
export function useCheckoutTexts() {
  const texts = useContext(CheckoutTextsContext);
  if (!texts) throw new Error('useCheckoutTexts outside CheckoutTextsProvider');
  return texts;
}

/** Checkout texts anywhere – the drawer: provider first, then the header store; `null` until hydrated. */
export function useDrawerTexts() {
  const fromProvider = useContext(CheckoutTextsContext);
  const fromStore = useCheckoutTextsStore(state => state.texts);
  return fromProvider ?? fromStore;
}

export function useAccountTexts() {
  const texts = useContext(AccountTextsContext);
  if (!texts) throw new Error('useAccountTexts outside AccountTextsProvider');
  return texts;
}
